import { t } from '@lingui/macro';

import { ServiceDescription } from '../../utilities/serviceDescriptionUtils';

import boardingImage from './assets/boarding.jpg';
import dogWalkingImage from './assets/dog-walking.jpg';
import doggyDayCareImage from './assets/doggy-day-care.jpg';
import dropInVisitsImage from './assets/drop-in-visits.jpg';
import groomingImage from './assets/grooming.jpg';
import houseSittingImage from './assets/house-sitting.jpg';
import petInsuranceImage from './assets/pet-insurance.jpg';
import trainingImage from './assets/training.jpg';

interface OverviewService {
  id: string;
  name: string;
  location: string | null;
  for: string;
  description: string;
  cta: string;
  imageUrl: string;
  href: string;
}

interface OverviewTab {
  id: string;
  label: string;
  services: OverviewService[];
}

const getOvernightServices = (): OverviewTab => ({
  id: 'overnight',
  label: t`Overnight care`,
  services: [
    {
      id: 'dog-boarding',
      name: t`Boarding`,
      location: t`In sitter's home`,
      for: t`For dogs & cats`,
      description: t`Book a cozy sleepover at a trusted sitter's house. Your pet gets personalized care. You get photo updates and can rest easy knowing they're safe and happy.`,
      cta: t`Explore Boarding`,
      imageUrl: boardingImage,
      href: '',
    },
    {
      id: 'house-sitting',
      name: t`House Sitting`,
      location: t`In your home`,
      for: t`For dogs & cats`,
      description: t`Travel stress-free knowing your pet and home are being looked after. Book a trusted house sitter on Rover to stay in your home and keep your pet's routine.`,
      cta: t`Explore House Sitting`,
      imageUrl: houseSittingImage,
      href: '',
    },
  ],
});

const getDaytimeServices = (): OverviewTab => ({
  id: 'daytime',
  label: t`Daytime care`,
  services: [
    {
      id: 'pet-sitting',
      name: t`Drop-In Visits`,
      location: t`In your home`,
      for: t`For dogs & cats`,
      description: t`Drop-in visits are perfect for cats and independent pups who just need a sitter to drop by your home one or more times a day. Your pets can get potty breaks, a clean litter box, playtime, meals, and more.`,
      cta: t`Explore Pet Sitting`,
      imageUrl: dropInVisitsImage,
      href: '',
    },
    {
      id: 'dog-walking',
      name: t`Dog Walking`,
      location: t`In your neighborhood`,
      for: t`For dogs`,
      description: t`Give active pups exercise, potty breaks, and fresh air with a trusted dog walker. Get updates, photos, and a map of the route after every walk.`,
      cta: t`Explore Dog Walking`,
      imageUrl: dogWalkingImage,
      href: '',
    },
    {
      id: 'doggy-day-care',
      name: t`Doggy Day Care`,
      location: t`In the sitter's home`,
      for: t`For dogs`,
      description: t`Give your dog a day of play at a trusted sitter's house. Drop them off in the morning and pick up a happy pup in the evening.`,
      cta: t`Explore Doggy Day Care`,
      imageUrl: doggyDayCareImage,
      href: '',
    },
  ],
});

const getOtherServices = (): OverviewTab => ({
  id: 'other',
  label: t`Other services`,
  services: [
    {
      id: 'grooming',
      name: 'Grooming',
      location: 'In your neighborhood',
      for: 'For dogs',
      description:
        'Your groomer bathes and grooms your pet at their home or yours. Your pet can get a full-service groom in a familiar environment.',
      cta: 'Explore Grooming',
      imageUrl: groomingImage,
      href: '/grooming/search/',
    },
    {
      id: 'dog-training',
      name: 'Training',
      location: null,
      for: 'For dogs',
      description:
        "Book in-person sessions with a certified trainer who can tailor lessons to your dog's needs. Work on basic manners or more advanced skills. Or find a professional virtual training through GoodPup.",
      cta: 'Explore Training',
      imageUrl: trainingImage,
      href: '',
    },
    {
      id: 'pet-insurance',
      name: 'Pet Insurance',
      location: null,
      for: 'For dogs & cats',
      description:
        "Explore pet insurance with our partner, Lemonade, to get a plan that gives your pet the care they need. You get peace of mind knowing you're financially prepared for any unexpected vet bills.",
      cta: 'Browse Pet Insurance',
      imageUrl: petInsuranceImage,
      href: '',
    },
  ],
});

const matchServiceWithAvailableServices = (
  service: OverviewService,
  availableServices: ServiceDescription[]
): OverviewService => {
  // Special case for pet-insurance
  if (service.id === 'pet-insurance') {
    return {
      ...service,
      href: '/lemonade-pet-insurance/?source=homepage',
    };
  }

  // Find matching service by serviceLandingType
  const matchedService = availableServices.find((s) => s.serviceLandingType === service.id);

  if (matchedService) {
    return {
      ...service,
      href: matchedService.href || '',
    };
  }

  return service;
};

export const getServiceOverviewCategories = (
  availableServices: ServiceDescription[],
  hasPetInsurance = true,
  shouldShowGroomingEntrypoints = false
): OverviewTab[] => {
  const overnightServices = getOvernightServices();
  const daytimeServices = getDaytimeServices();
  const otherServices = getOtherServices();

  // Match services with available services and add hrefs
  const matchedOvernightServices = {
    ...overnightServices,
    services: overnightServices.services
      .map((service) => matchServiceWithAvailableServices(service, availableServices))
      .filter((service) => service.href), // Only include services that have an href
  };

  const matchedDaytimeServices = {
    ...daytimeServices,
    services: daytimeServices.services
      .map((service) => matchServiceWithAvailableServices(service, availableServices))
      .filter((service) => service.href), // Only include services that have an href
  };

  const matchedOtherServices = {
    ...otherServices,
    services: otherServices.services
      .map((service) => matchServiceWithAvailableServices(service, availableServices))
      .filter((service) => {
        // Filter out pet insurance if not available in the country
        if (service.id === 'pet-insurance' && !hasPetInsurance) {
          return false;
        }
        // Filter out grooming if entrypoints flag is not enabled
        if (service.id === 'grooming' && !shouldShowGroomingEntrypoints) {
          return false;
        }
        return service.href;
      }),
  };

  const allServicesContent = [
    ...matchedOvernightServices.services,
    ...matchedDaytimeServices.services,
    ...matchedOtherServices.services,
  ];

  const allServices: OverviewTab = {
    id: 'all',
    label: t`All services`,
    services: allServicesContent,
  };

  const tabs = [matchedOvernightServices, matchedDaytimeServices, matchedOtherServices];
  tabs.push(allServices);

  return tabs;
};
